import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  Avatar,
  Typography,
  Statistic,
  Progress,
  Tooltip,
  Badge,
  Popconfirm,
  message
} from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  SafetyCertificateOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

const PersonnelPage = () => {
  const { user } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingPerson, setEditingPerson] = useState(null);

  // Mock data for personnel
  const mockPersonnel = [
    {
      id: 1,
      name: 'John Smith',
      position: 'Mine Manager',
      department: 'Management',
      status: 'active',
      email: 'john.smith@mine.com',
      phone: '+27 11 123 4567',
      avatarColor: '#1890ff',
      skills: ['Leadership', 'Planning', 'Safety'],
      certifications: ['Mine Manager Certificate', 'First Aid'],
      joinDate: '2022-03-15'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      position: 'Safety Officer',
      department: 'Safety',
      status: 'active',
      email: 'sarah.j@mine.com',
      phone: '+27 11 234 5678',
      avatarColor: '#52c41a',
      skills: ['Safety Compliance', 'Risk Assessment', 'Training'],
      certifications: ['Safety Officer Certificate', 'Fire Safety'],
      joinDate: '2023-01-20'
    },
    {
      id: 3,
      name: 'Mike Brown',
      position: 'Equipment Operator',
      department: 'Operations',
      status: 'active',
      email: 'mike.b@mine.com',
      phone: '+27 11 345 6789',
      avatarColor: '#faad14',
      skills: ['Excavator Operation', 'Maintenance', 'Safety'],
      certifications: ['Heavy Equipment License', 'Defensive Driving'],
      joinDate: '2023-05-10'
    },
    {
      id: 4,
      name: 'Lisa Wang',
      position: 'Geologist',
      department: 'Geology',
      status: 'on_leave',
      email: 'lisa.w@mine.com',
      phone: '+27 11 456 7890',
      avatarColor: '#f5222d',
      skills: ['Core Sampling', 'Data Analysis', 'Reporting'],
      certifications: ['Professional Geologist', 'GIS Specialist'],
      joinDate: '2022-11-30'
    },
    {
      id: 5,
      name: 'David Wilson',
      position: 'Maintenance Technician',
      department: 'Maintenance',
      status: 'active',
      email: 'david.w@mine.com',
      phone: '+27 11 567 8901',
      avatarColor: '#722ed1',
      skills: ['Mechanical Repair', 'Electrical', 'Welding'],
      certifications: ['Trade Certificate', 'Forklift License'],
      joinDate: '2023-03-22'
    },
    {
      id: 6,
      name: 'Emma Davis',
      position: 'Production Supervisor',
      department: 'Operations',
      status: 'inactive',
      email: 'emma.d@mine.com',
      phone: '+27 11 678 9012',
      avatarColor: '#13c2c2',
      skills: ['Team Management', 'Production Planning', 'Quality Control'],
      certifications: ['Supervisor Certificate', 'Six Sigma'],
      joinDate: '2021-08-14'
    }
  ];

  useEffect(() => {
    fetchPersonnel();
  }, []);

  const fetchPersonnel = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setPersonnel(mockPersonnel);
      setLoading(false);
    }, 500);
  };

  const handleAddPerson = () => {
    form.resetFields();
    setEditingPerson(null);
    setModalVisible(true);
  };

  const handleEditPerson = (record) => {
    form.setFieldsValue(record);
    setEditingPerson(record);
    setModalVisible(true);
  };

  const handleDeletePerson = (id) => {
    setPersonnel(personnel.filter(person => person.id !== id));
    message.success('Personnel record deleted successfully');
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      if (editingPerson) {
        // Update existing
        setPersonnel(personnel.map(person => 
          person.id === editingPerson.id ? { ...person, ...values } : person
        ));
        message.success('Personnel record updated successfully');
      } else {
        // Add new
        const newPerson = {
          id: personnel.length + 1,
          ...values,
          avatarColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
          joinDate: new Date().toISOString().split('T')[0]
        };
        setPersonnel([...personnel, newPerson]);
        message.success('Personnel record added successfully');
      }
      setModalVisible(false);
    }).catch(err => {
      console.log('Validation failed:', err);
    });
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'active':
        return <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>;
      case 'on_leave':
        return <Tag icon={<ClockCircleOutlined />} color="warning">On Leave</Tag>;
      case 'inactive':
        return <Tag icon={<CloseCircleOutlined />} color="error">Inactive</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getDepartmentColor = (department) => {
    const colors = {
      'Management': 'blue',
      'Safety': 'green',
      'Operations': 'orange',
      'Geology': 'red',
      'Maintenance': 'purple'
    };
    return colors[department] || 'default';
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            style={{ backgroundColor: record.avatarColor }}
            icon={<UserOutlined />}
          >
            {text.charAt(0)}
          </Avatar>
          <div>
            <div><strong>{text}</strong></div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (department) => (
        <Tag color={getDepartmentColor(department)}>{department}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Skills',
      dataIndex: 'skills',
      key: 'skills',
      render: (skills) => (
        <div style={{ maxWidth: '200px' }}>
          {skills.map(skill => (
            <Tag key={skill} style={{ marginBottom: '2px' }}>{skill}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditPerson(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this personnel record?"
            description="Are you sure you want to delete this record?"
            onConfirm={() => handleDeletePerson(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const departmentStats = [
    { department: 'Operations', count: 24, color: '#1890ff' },
    { department: 'Maintenance', count: 12, color: '#52c41a' },
    { department: 'Safety', count: 8, color: '#faad14' },
    { department: 'Geology', count: 6, color: '#f5222d' },
    { department: 'Management', count: 4, color: '#722ed1' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>
            <TeamOutlined style={{ marginRight: '12px' }} />
            Personnel Management
          </Title>
          <Text type="secondary">Manage mine personnel, roles, and assignments</Text>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddPerson}
          >
            Add Personnel
          </Button>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Personnel"
              value={personnel.length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Staff"
              value={personnel.filter(p => p.status === 'active').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Departments"
              value={5}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Certifications"
              value={24}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Department Distribution */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card title="Department Distribution">
            <Row gutter={[16, 16]}>
              {departmentStats.map(dept => (
                <Col xs={24} sm={12} md={8} lg={4} key={dept.department}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        borderRadius: '50%', 
                        backgroundColor: dept.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        color: 'white',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                        {dept.count}
                      </div>
                      <Text strong>{dept.department}</Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Personnel Table */}
      <Card title="Personnel List">
        <Table
          columns={columns}
          dataSource={personnel}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingPerson ? 'Edit Personnel' : 'Add New Personnel'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          name="personnel_form"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Position"
                rules={[{ required: true, message: 'Please enter position' }]}
              >
                <Input placeholder="Enter position" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
                rules={[{ required: true, message: 'Please select department' }]}
              >
                <Select placeholder="Select department">
                  <Option value="Management">Management</Option>
                  <Option value="Safety">Safety</Option>
                  <Option value="Operations">Operations</Option>
                  <Option value="Geology">Geology</Option>
                  <Option value="Maintenance">Maintenance</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="active">Active</Option>
                  <Option value="on_leave">On Leave</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="skills"
            label="Skills"
          >
            <Select
              mode="tags"
              placeholder="Add skills"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="certifications"
            label="Certifications"
          >
            <Select
              mode="tags"
              placeholder="Add certifications"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PersonnelPage;